import { collection, query, where, getDocs, doc, setDoc, orderBy, writeBatch, Timestamp } from "firebase/firestore";

import { criteriaExtractionGemini, firestoreDb, verificationGemini } from "../firebase";
import { LLM_CHATS, THREAD_ID, EQUALS, USER, MODEL, RESPONSE_TO_USER, EMPTY_STRING, GET_CRITERIA_PT, CREATED_AT, ASC } from "../constants";

type Role = "user" | "model" | "function" | "system";

const getLlmChats = async (threadId: string) : Promise<LlmChatMsg[]> => {
  const llmChatSnapshot = await getDocs(query(
    collection(firestoreDb, LLM_CHATS),
    where(THREAD_ID, EQUALS, threadId),
    orderBy(CREATED_AT, ASC)
  ));
  return llmChatSnapshot.docs.map(doc => doc.data() as LlmChatMsg);
}

const updateWithTransformationId = async (threadId: string, transformationId: string) : Promise<void> => {
  try {
    const llmChatSnapshot = await getDocs(query(
      collection(firestoreDb, LLM_CHATS),
      where(THREAD_ID, EQUALS, threadId)
    ));
    const batch = writeBatch(firestoreDb);
    llmChatSnapshot.docs.forEach((document) => {
      const docRef = doc(firestoreDb, LLM_CHATS, document.id);
      batch.update(docRef, { transformationId });
    });
    await batch.commit();
  } catch (error) {
    console.error('Error updating llmchats with transformationId:', error);
  }
}

const addLlmChat = async (threadId: string, userId: string, role: string, content: string) : Promise<void> => {
  const newLlmChatRef = doc(collection(firestoreDb, LLM_CHATS));
    const newLlmChat: LlmChatMsg = {
      id: newLlmChatRef.id,
      threadId,
      role,
      content,
      userId,
      createdAt: Timestamp.now()
    };
    await setDoc(newLlmChatRef, newLlmChat);
}

const chatToVerifyPrompt = async (userMsg: string, threadId: string, userId: string) : Promise<any> => {
  try {
    const conversationHistory = await getLlmChats(threadId);
    const history = conversationHistory.map((llmChatMsg: LlmChatMsg) => ({
      role: llmChatMsg.role as Role,
      parts: [{ text: llmChatMsg.content }]
    }));
    const chatThread = verificationGemini.startChat({
      history,
      generationConfig: {
        maxOutputTokens: 100,
      },
    });
    const llmRes = await chatThread.sendMessage(userMsg);
    await addLlmChat(threadId, userId, USER, userMsg);
    const responseText = llmRes.response.text().replace(/```json\n?|\n?```/g, '').trim();
    let resObj: any;
    try {
      resObj = JSON.parse(responseText);
    } catch {
      // todo: need to fix this or remove
      resObj = {
        [RESPONSE_TO_USER]: responseText
      };
    }
    if (RESPONSE_TO_USER in resObj) {
      await addLlmChat(threadId, userId, MODEL, resObj[RESPONSE_TO_USER]);
    }
    return resObj;
  } catch (error) {
    console.error('Error retrieving LLM response:', error.message);
  }
  return null;
}

const extractCriteria = async (threadId: string) : Promise<any> => {
  const conversationHistory = await getLlmChats(threadId);
  let history: string = EMPTY_STRING;
  conversationHistory.map((llmChatMsg: LlmChatMsg) => {
    if (llmChatMsg.role === USER) {
      history += `User: ${llmChatMsg.content}\n`;
    } else {
      history += `Model: ${llmChatMsg.content}\n`;
    }
  });
  const llmRes = await criteriaExtractionGemini.generateContent(GET_CRITERIA_PT(history));
  return JSON.parse(llmRes.response.text().replace(/```json\n?|\n?```/g, '').trim());
}

const LlmService = {
  chatToVerifyPrompt,
  extractCriteria,
  updateWithTransformationId
}

export default LlmService;
